import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { CpqFacade } from '../../facades/cpq.facade';
import { OrdersFacade } from '../../facades/orders.facade';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { EScenarioType } from '../../configurations/order-status';
import { FileType, FileUtils } from '../../utils/file.utils';
import { IStoreAddress } from '../../models/settings.model';
import { MathUtils } from '../../utils/math.utils';
import { I18nService } from '../../services';

@Component({
  selector: 'app-thank-you-for-quote',
  templateUrl: './thank-you-for-quote.component.html',
  styleUrls: ['./thank-you-for-quote.component.scss'],
  standalone: false,
})

export class ThankYouForQuoteComponent implements OnInit, OnDestroy {
  private translate: I18nService = inject(I18nService);
  private cpqFacade: CpqFacade = inject(CpqFacade);
  private ordersFacade: OrdersFacade = inject(OrdersFacade);
  private configFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private router: Router = inject(Router);

  storeAddress!: IStoreAddress;
  phone!: string;
  email!: string;
  phoneNumberFormat!: string;
  prefix!: string;
  delimiter!: string;
  scenarioType: EScenarioType = EScenarioType.CPQ;
  currentStatus!: string;
  details: any;
  nickname!: string;
  orderDetails: any;
  cartName!: string;
  cpqQuotePrice!: number;
  loadingPageData: boolean = true;
  quoteDocumentGenerationInProgress: boolean = false;

  private unsubscribe$: Subject<void> = new Subject<void>();
  private docDownloadErrorMsgKey: string = 'configuration-summary.doc-download-error-message';
  private docDownloadErrorMsg!: string;

  ngOnInit(): void {
    this.setDownloadErrorMessage();
    this.selectLastSubmittedOrder();
    this.getStoreAddressInfo();
    this.detectBrowserBackBtn();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  calculateEquType(items: any): number {
    const materialNumbers: unknown[] = [...new Set(items.map((item: any) => item.systemDetails.materialNumber))];
    return materialNumbers ? materialNumbers.length : 0;
  }

  downloadDocumentation(): void {
    this.quoteDocumentGenerationInProgress = true;
    this.cpqFacade.getCpqQuotePdfFile(this.orderDetails.orderReference)
      .pipe(take(1))
      .subscribe({
          next: file => {
            FileUtils.saveAndOpenFile(file, FileType.PDF, `Quote_${this.orderDetails.orderReference}.pdf`);
            this.quoteDocumentGenerationInProgress = false;
          },
          error: () => {
            this.configFacade.setAlert({
              type: 'error',
              message: this.docDownloadErrorMsg,
            });
            this.quoteDocumentGenerationInProgress = false;
          },
        },
      );
  }

  startNewQuote(): void {
    this.cpqFacade.thankYouPageStartNewQuote();
  }

  viewPurchaseActivity(): void {
    this.cpqFacade.thankYouPageViewPurchaseActivity();
  }

  private detectBrowserBackBtn(): void {
    this.router.events.pipe(takeUntil(this.unsubscribe$))
      .subscribe((event: any) => {
        if (event.navigationTrigger === 'popstate') {
          this.viewPurchaseActivity();
        }
      });
  }

  private retrieveOrderDetails(id: string): void {
    this.ordersFacade.getOrderById(id)
      .pipe(take(1))
      .subscribe(response => {
        const {items, stateDisplayName, quoteName, totals} = response.data.attributes;
        this.details = items;
        this.currentStatus = stateDisplayName ?? '';
        this.cartName = quoteName ?? '';
        this.nickname = MathUtils.checkIfNumeric(this.cartName) ? '' : '(' + this.cartName + ')';
        this.cpqQuotePrice = totals?.grandTotal ?? 0;
        this.loadingPageData = false;
      });
  }

  private getStoreAddressInfo(): void {
    const storeAddress: IStoreAddress | undefined = this.configFacade.getCurrentStoreAddress();
    if (storeAddress) {
      this.storeAddress = storeAddress;
      this.phone = this.storeAddress.phoneNumber ?? '';
      this.email = this.storeAddress.emailAddress ?? '';
      this.phoneNumberFormat = this.storeAddress.phoneNumberFormat ?? '';
      this.prefix = this.storeAddress.prefix ?? '';
      this.delimiter = this.storeAddress.delimiter ?? '';
    }
  }

  private selectLastSubmittedOrder(): void {
    this.cpqFacade.selectLastSubmittedOrder()
      .pipe(take(1))
      .subscribe(order => {
        if (order) {
          this.orderDetails = order;
          this.retrieveOrderDetails(this.orderDetails.orderReference);
        } else {
          this.cpqFacade.thankYouPageRedirectToLastPage();
        }
      });
  }

  private setDownloadErrorMessage(): void {
    this.translate.getTranslationByKey(this.docDownloadErrorMsgKey)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(message => this.docDownloadErrorMsg = message);
  }
}
